import {
  PayableRow,
  PayableStatus,
  UpdatePayableWorkflowStatusRequestPayerApprovalStatus
} from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { payablesApi } from "@api";
import { queriesPayableRows, queriesPayables } from "@query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../BulkActions";
import { QUERY_PAYABLE } from "../../../query/payments/keys";

const invalidateAfterMutation = (successItems: PayableRow[]) => {
  WSQueryCache.invalidateQueries(
    queriesPayableRows.searchPayableRows.queryKeyBase
  );
  WSQueryCache.invalidateQueries(queriesPayables.getPayable.queryKeyBase);
  successItems.forEach(payable => {
    if (payable.id) {
      WSQueryCache.invalidateQueries([QUERY_PAYABLE, payable.id]);
    }
  });
};

const processApprovePayables = (
  items: PayableRow[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> => {
  const approvable = items.filter(item => item.status !== PayableStatus.PAID);
  const skipped = items.length - approvable.length;

  const bulkAction = bulkActionFactory<PayableRow>({
    onProgress,
    action: async item => {
      if (!item.id) {
        throw new Error("Payable is missing id");
      }
      return payablesApi.updatePayableWorkflowStatus({
        payableId: item.id,
        updatePayableWorkflowStatusRequest: {
          payerApprovalStatus:
            UpdatePayableWorkflowStatusRequestPayerApprovalStatus.APPROVED
        }
      });
    },
    onFinish: ({ successItems }) => {
      invalidateAfterMutation(successItems);
      onSuccess?.();
    }
  });

  return bulkAction.run(approvable).then(({ successItems, failedItems }) => ({
    total: items.length,
    processed: successItems.length + failedItems.length + skipped,
    successful: successItems.length,
    failed: failedItems.length
  }));
};

export const useBulkApprovePayables = () => {
  const runBulkAction = useForegroundBulkAction();

  return (items: PayableRow[], onSuccess?: () => void) => {
    const pluralized = `payable${items.length > 1 ? "s" : ""}`;

    return runBulkAction({
      actionName: "Approve payables",
      itemCount: items.length,
      confirmDescription: `Approve ${pluralized} to be paid`,
      confirmLabel: "Approve",
      prepare: async () => items,
      process: async (data, onProgress) =>
        processApprovePayables(data, onProgress, onSuccess)
    });
  };
};
